/**
 * RESPONSIBILITY: when an event happens, in words, and how to sort by it.
 *
 * An event says when it happens in one of three ways, and the page has to cope with all
 * of them: a fixed date, a repeat rule, or neither — "Dates vary", which is a real
 * answer the requirements doc asks for.
 *
 * A repeat is resolved with the framework's own nextRepeat, so what the page shows
 * and what a filter sorts by are the same number.
 */
import { hasTime, isComplete, nextRepeat, readRepeat, type RepeatRule } from '@framework/core';
import type { EventRow } from '@app/sections';
import { rankNow } from '@app/sections/events/priority-now';

const FORMAT = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
});
const TIME = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });

/**
 * One stored moment, in words. Here beside the other two formatters and not beside the
 * band that needed it, because a third date format on the same page is how a site starts
 * saying "Sat 26 Sep" in one place and "26/09/2026" in another.
 */
export function momentText(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return iso.includes('T') ? `${FORMAT.format(d)} · ${TIME.format(d)}` : FORMAT.format(d);
}

/**
 * The event's repeat, when it has one that can fall. A rule half chosen — "every month",
 * no day yet — is somebody part-way through, and the date they already gave still stands.
 */
function ruleOf(e: EventRow): RepeatRule | null {
  const r = readRepeat(e.repeats);
  return r && isComplete(r) ? r : null;
}

/**
 * The moment this event next happens, or null when it never will again.
 *
 * Sorting and filtering both use this, so a repeating event takes its place among the
 * fixed-date ones rather than sitting at the bottom of the list forever.
 */
export function nextAt(e: EventRow): Date | null {
  // Asked from the start of today, so today's gathering is still "next" while it is on —
  // asked from now, the prayer call vanished to next week the minute it began.
  const rule = ruleOf(e);
  if (rule) {
    const now = new Date();
    return nextRepeat(rule, new Date(now.getFullYear(), now.getMonth(), now.getDate()));
  }
  if (e.startsAt) {
    const d = new Date(e.startsAt);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Hand-placed first, then soonest.
 *
 * The rank is 0 for almost everything, which means "sort me by date like the rest". A
 * positive number pins the event above all of those, in the order given — for the weeks
 * when what matters is not what happens next.
 *
 * It comes from rankNow and not from `position`, because an event may say what it is
 * worth THIS WEEK. Read on every comparison rather than precomputed: the list is dozens
 * of rows, and a rank cached across midnight is a rank that is wrong by morning.
 *
 * Anything with no date at all sorts last rather than disappearing.
 */
export const byNext = (a: EventRow, b: EventRow): number => {
  const pa = rankNow(a);
  const pb = rankNow(b);
  if (pa !== pb) return (pa || Infinity) - (pb || Infinity);

  const x = nextAt(a)?.getTime() ?? Infinity;
  const y = nextAt(b)?.getTime() ?? Infinity;
  return x - y;
};

/**
 * Whether a VISITOR is shown this event.
 *
 * ONE SWITCH, then one window, and nothing else.
 *
 *   · `showInMain` is the switch. It is on unless somebody turns it off.
 *   · the window is [showFrom, showUntil]. A missing start is unbounded before, a
 *     missing end is unbounded after, so an event with neither is always shown.
 *
 * Silence means ALWAYS ON. It briefly meant "not ready" — an event needed a date before
 * anybody could see it — which turned every half-entered event into an invisible one and
 * gave the same fact two switches to disagree over.
 *
 * Nothing here hides anything from an EDITOR. Every list passes `canEdit`, and a row a
 * visitor cannot see is shown to them marked rather than dropped.
 */
export function isVisible(e: EventRow, now = new Date()): boolean {
  if (e.showInMain === false) return false;
  if (e.showFrom && now < startOfDay(e.showFrom)) return false;
  if (e.showUntil && now > endOfDay(e.showUntil)) return false;
  return true;
}

/**
 * `YYYY-MM-DD` as a moment in the READER'S OWN DAY, at both ends.
 *
 * `new Date('2026-09-15')` is UTC midnight — a date-only string is specified that way —
 * while `new Date('2026-09-15T23:59')` is local. So the two ends of one window were being
 * read in two different time zones, and west of Greenwich a window set to open tomorrow
 * opened this evening. An editor who types a date means the day they are standing in.
 */
function parts(iso: string): [number, number, number] {
  const [y, m, d] = iso.split('-').map(Number);
  return [y, (m || 1) - 1, d || 1];
}

const startOfDay = (iso: string): Date => new Date(...parts(iso));
const endOfDay = (iso: string): Date => {
  const [y, m, d] = parts(iso);
  return new Date(y, m, d, 23, 59, 59, 999);
};

export function whenText(e: EventRow): string {
  const at = nextAt(e);
  if (!at) return e.dateNote || 'Dates vary';

  const day = FORMAT.format(at);
  // A date with no time of day is a date, not midnight.
  const rule = ruleOf(e);
  const timed = rule ? hasTime(rule) : /T\d\d:\d\d/.test(e.startsAt ?? '');
  return timed ? `${day} · ${TIME.format(at)}` : day;
}
